import { useLanguage } from "@/contexts/LanguageContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RefreshCw, Clock, Info } from "lucide-react";
import { ContractWizardFormData } from "./types";

interface ServicesStepProps {
  formData: ContractWizardFormData;
  updateFormData: (updates: Partial<ContractWizardFormData> | ((prev: ContractWizardFormData) => ContractWizardFormData)) => void;
}

// Step 3 of the contract wizard.
//
// What used to live here: contract-level utility configs (electricity /
// water / gas / internet) + a single contract-level maintenance fee.
// Those have moved per-unit in Step 1 (each unit row now captures all 8
// annual services with their own payer). This step now only carries
// contract-level terms: auto-renewal, notice period, and free-text notes.
//
// The maintenanceFee + maintenanceFeePayer + utility configs on
// ContractWizardFormData are kept on the form data shape so legacy
// callers don't crash, but they're no longer collected from the user
// here — the wizard submit ignores them when sending p_units in the new
// shape.
export function ServicesStep({ formData, updateFormData }: ServicesStepProps) {
  const { t } = useLanguage();

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/30 text-sm text-muted-foreground">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <p>
          {t(
            "خدمات العقد (كهرباء، ماء، غاز، إنترنت، صيانة، إلخ.) تُحدَّد لكل وحدة في الخطوة الأولى.",
            "Contract services (electricity, water, gas, internet, maintenance, etc.) are configured per unit in Step 1."
          )}
        </p>
      </div>

      {/* Auto Renewal Toggle */}
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div className="flex items-center gap-3">
          <RefreshCw className="h-5 w-5 text-primary" />
          <div>
            <Label htmlFor="autoRenewal" className="cursor-pointer font-medium">
              {t("التجديد التلقائي", "Auto Renewal")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("يتجدد العقد تلقائياً عند انتهائه", "Contract automatically renews when it expires")}
            </p>
          </div>
        </div>
        <Switch
          id="autoRenewal"
          checked={formData.autoRenewal}
          onCheckedChange={(checked) => updateFormData({ autoRenewal: checked })}
        />
      </div>

      {/* Notice Period */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          {t("فترة الإشعار (بالأيام)", "Notice Period (Days)")}
        </Label>
        <Input
          type="number"
          min="0"
          value={formData.noticePeriod}
          onChange={(e) => updateFormData({ noticePeriod: e.target.value })}
          placeholder="30"
        />
        <p className="text-xs text-muted-foreground">
          {t(
            "عدد الأيام المطلوبة للإشعار قبل إنهاء أو عدم تجديد العقد",
            "Days required for notice before ending or not renewing the contract"
          )}
        </p>
      </div>

      {/* Additional Notes */}
      <div className="space-y-2">
        <Label>{t("شروط وملاحظات إضافية", "Additional Terms & Notes")}</Label>
        <Textarea
          data-testid="contract-notes-input"
          value={formData.notes}
          onChange={(e) => updateFormData({ notes: e.target.value })}
          placeholder={t("أضف أي شروط أو ملاحظات إضافية...", "Add any additional terms or notes...")}
          rows={4}
        />
      </div>
    </div>
  );
}
